/**
 * `strategiesDetail` namespace — English canonical source.
 *
 * Owned by the Strategy Detail team: the DSL config viewer, run-history timeline,
 * decision-replay trace, and per-strategy P&L panel (ARCHITECTURE.md §8). Keys are
 * dot-free camelCase; values are the canonical English text. Interpolated copy uses
 * `{name}` tokens resolved via `t(key, { name })`.
 *
 * Scope boundaries (do NOT re-declare here):
 *   • DSL node renderings (comparator/operand/schedule/action labels) live in
 *     `features/strategies/dsl.ts` and stay verbatim — they are spec text, not chrome.
 *   • Status/phase/outcome badge vocab lives in `features/strategies/meta.tsx`.
 *   • Raw DSL enum values (execution `mode`, universe `role`, `onceScope`, run
 *     `state`/`side`/`verdict`, indicator specs) stay visible as-is next to their
 *     translated human labels.
 *   • Shared status/action copy is reused from `common.*` (e.g. `common.loading`).
 *
 * `ko/strategiesDetail.ts` mirrors this shape (a missing key is a type error).
 */
export const strategiesDetail = {
  // ── DSL config viewer · fallback ──
  rawTitle: "Strategy definition",
  rawSubtitle: "Raw document (failed validation)",

  // ── DSL config viewer · metadata ──
  definitionTitle: "Definition",
  definitionSubtitle: "Schema v{version} · {id}",
  fieldName: "Name",
  fieldRiskLevel: "Risk level",
  fieldAuthor: "Author",
  fieldTags: "Tags",

  // ── DSL config viewer · schedule / universe / rules ──
  scheduleTitle: "Schedule",
  universeTitle: "Universe",
  universeSubtitle: "{count} traded symbol(s)",
  rulesTitle: "Rules",
  rulesSubtitle: "{count} trigger rule(s)",
  ruleOnce: "once · {scope}",
  ruleCooldown: "cooldown {seconds}s",
  ruleWhen: "When",
  ruleThen: "Then",

  // ── DSL config viewer · risk policy ──
  riskTitle: "Risk",
  riskSubtitle: "Budget and guard rails (§0.5)",
  fieldBudget: "Budget",
  fieldMaxPositionValue: "Max position value",
  fieldMaxPositionPct: "Max position %",
  fieldDailyLossCap: "Daily loss cap",
  fieldCooldown: "Cooldown",
  fieldMaxOpenOrders: "Max open orders",
  fieldHaltOnBreach: "Halt on breach",
  yes: "Yes",
  no: "No",

  // ── DSL config viewer · execution policy ──
  executionTitle: "Execution",
  executionSubtitle: "Mode: {mode}",
  executionAuto:
    "Orders execute automatically when a rule fires (still subject to dry-run and the global arm switch).",
  fieldApprovalTimeout: "Approval timeout",
  fieldOnTimeout: "On timeout",
  fieldModel: "Model",
  fieldTimeout: "Timeout",
  fieldCanVetoResize: "Can veto / resize",
  advisorVeto: "veto",
  advisorResize: "resize",
  fieldMaxResizeDown: "Max resize down",
  fieldPromptContext: "Prompt context",
  fieldAdvisorInstructions: "Advisor guidance",
  advisorInstructionsShowMore: "Show full guidance",
  advisorInstructionsShowLess: "Show less",

  // ── DSL config viewer · notifications ──
  notificationsTitle: "Notifications",
  notificationsSubtitle: "Per-strategy channel routing",
  noChannels: "No channels configured",

  // ── decision trace ──
  decisionTraceTitle: "Decision trace",
  decisionTraceEmpty: "No decision rows were recorded for this run.",
  decisionTraceSubtitle: "{count} audited step(s)",
  blockIndicators: "Indicators",
  blockConditionTrace: "Condition trace",
  blockRiskChecks: "Risk checks",
  checkPass: "pass",
  checkFail: "fail",
  blockAiVerdict: "AI advisor verdict",
  blockOrderIntent: "Order intent",
  intentHighValue: "high value · approval",
  decisionRowEmpty: "No structured inputs recorded for this step.",

  // ── run-history timeline ──
  runHistoryTitle: "Run history",
  runHistorySubtitle: "Every tick — signals, blocks, and fills",
  runHistoryError: "Could not load run history.",
  runHistoryEmpty: "No runs yet. This strategy has not evaluated a tick.",
  loadOlderRuns: "Load older runs",
  viewAllRuns: "View all runs",

  // ── strategy portfolio (ledger-scoped) panel ──
  portfolioTitle: "Strategy positions",
  portfolioSubtitle: "Ledger-scoped book — what the risk gate actually sees",
  portfolioError: "Could not load the strategy portfolio.",
  portfolioValueLabel: "Strategy value",
  portfolioAllocated: "Allocated",
  portfolioUnrealized: "Unrealized P&L",
  portfolioRealizedToday: "Realized today",
  portfolioColSymbol: "Symbol",
  portfolioColQty: "Qty",
  portfolioColSellable: "Sellable",
  portfolioColValue: "Value",
  portfolioColPnlPct: "P&L %",
  portfolioUnknown: "unknown",
  portfolioAllFlat:
    "The strategy holds no ledger positions — external account holdings are deliberately invisible here.",

  // ── strategy P&L panel ──
  performanceTitle: "Performance",
  performanceSubtitle: "Realized P&L, win-rate, and payoff",
  performanceError: "Could not load performance.",
  statRealizedPnl: "Realized P&L",
  statTrades: "{count} trades",
  statWinRate: "Win rate",
  statWinLoss: "{wins}W / {losses}L",
  statPayoffRatio: "Payoff ratio",
  statPayoffCaption: "avg win / avg loss",
  statTradesLabel: "Trades",
  statTradesCaption: "closed positions",
  profit: "{amount} profit",
  loss: "{amount} loss",
} as const;

export type StrategiesDetailKey = keyof typeof strategiesDetail;
